import { ConstructionSiteList, PAGES } from "@dune/types";
import {
	Button,
	Card,
	Separator,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@dune/ui";
import { Users } from "lucide-react";
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGetConstructionSiteSubmissionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { BuildersTableReception } from "../../../../component/submission-participants/BuildersTableReception";
import { DesignersTableReception } from "../../../../component/submission-participants/DesignersTableReception";
import { MaterialsTableReception } from "../../../../component/submission-participants/MaterialsTableReception";
import { useDocumentSelection } from "../../../../hooks/panel-sync/useDocumentSelection";
import { useReceptionParticipantsView } from "../../../../hooks/useReceptionParticipantsView";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useDocumentStore } from "../../../../stores/document.store";
import BuildersSection from "../intervenants/BuildersSection";
import DesignersSection from "../intervenants/DesignersSection";
import MaterialsSection from "../intervenants/MaterialsSection";
import { useShouldDisplayHTAmount } from "../../../../hooks/useParticipantToSubmissionForm";
import { useMe } from "../../../../api/queries/users.queries";
import { RoleEnum } from "@dune/vars";

interface ParticipantsTabProps {
	constructionSite: ConstructionSiteList;
}

const ParticipantsTab: React.FC<ParticipantsTabProps> = ({
	constructionSite,
}) => {
	const { constructionSiteId: constructionSiteIdString } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const { me: user } = useMe();
	const isInsurer = user?.role === RoleEnum.INSURER;
	const isDune = user?.role === RoleEnum.DUNE;
	const constructionSiteId = Number.parseInt(constructionSiteIdString);
	const navigate = useNavigate();

	const [activeTab, setActiveTab] = useState<"souscription" | "reception">(
		"souscription",
	);
	const handleTabChange = (value: string) =>
		setActiveTab(value as "souscription" | "reception");

	const {
		data: submissionData,
		isLoading,
		error,
	} = useGetConstructionSiteSubmissionParticipants(constructionSiteId);

	const receptionSections = useReceptionParticipantsView(constructionSiteId);

	const [selectedParticipantId, setSelectedParticipantId] = useState<
		number | null
	>(null);

	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);

	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	useDocumentSelection({
		entityType: "CONSTRUCTION_SITE",
		entityId: constructionSiteId,
	});

	const handleParticipantSelect = useCallback(
		(participantId: number, isSelected: boolean) => {
			setSelectedParticipantId(isSelected ? participantId : null);
		},
		[],
	);

	const handleDocumentClick = useCallback(
		async (file: {
			id: string;
			key: string;
			bucket: string;
			mimeType: string;
			name: string;
		}) => {
			const docId = file.id;
			if (loadingDocumentId === docId) return;

			if (!file.key || file.key.trim() === "") {
				return;
			}

			setLoadingDocumentId(docId);

			try {
				const url = await getDocumentUrl({
					bucket: file.bucket,
					key: file.key,
				});

				if (url) {
					const selectDocument = useDocumentStore.getState().selectDocument;
					selectDocument(url, docId, file.mimeType, file.name);
				}
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'URL du document:",
					error,
				);
			} finally {
				setLoadingDocumentId(null);
			}
		},
		[loadingDocumentId],
	);

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
					<div className="h-32 bg-gray-200 rounded" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Card className="p-6">
					<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
						<Users className="h-5 w-5 text-blue-600" />
						Intervenants
					</h3>
					<p className="text-red-500">
						Erreur lors du chargement des intervenants.
					</p>
				</Card>
			</div>
		);
	}

	const statusAllowsEdit =
		constructionSite.status === "NEW" ||
		constructionSite.status === "IN_PROGRESS" ||
		constructionSite.status === "TO_ANALYZE";

	return (
		<div className="h-full">
			<div className="flex flex-col gap-6">
				<div className="flex justify-between items-center">
					<div className="text-blue-badge-text text-2xl font-semibold">
						Intervenants
					</div>
					{(isDune ||
						(statusAllowsEdit &&
							!isInsurer &&
							!constructionSite.isSkipped)) && (
						<Button
							type="button"
							size="lg"
							onClick={() => {
								if (activeTab === "reception") {
									navigate(`/projects/${constructionSiteId}/reception?step=3`);
								} else {
									navigate(
										PAGES.PROJECTS.submission.withParams(
											constructionSiteId.toString(),
											4,
										),
									);
								}
							}}
						>
							Modifier les intervenants
						</Button>
					)}
				</div>

				<Tabs
					value={activeTab}
					onValueChange={handleTabChange}
					className="w-full"
				>
					<TabsList>
						<TabsTrigger value="souscription" variant="compact">
							Souscription
						</TabsTrigger>
						<TabsTrigger value="reception" variant="compact">
							Réception
						</TabsTrigger>
					</TabsList>

					<TabsContent value="souscription" className="mt-4">
						<div className="space-y-6">
							{submissionData ? (
								<>
									<DesignersSection
										submissionParticipants={submissionData.participantToConstructionSiteSubmissions.filter(
											(p) => p.type === "DESIGNER",
										)}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
									/>
									<Separator className="my-8" />
									<BuildersSection
										submissionParticipants={submissionData.participantToConstructionSiteSubmissions.filter(
											(p) => p.type === "BUILDER",
										)}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
									/>
									<Separator className="my-8" />
									<MaterialsSection
										submissionParticipants={submissionData.participantToConstructionSiteSubmissions.filter(
											(p) => p.type === "MATERIAL",
										)}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
									/>
									<Separator className="my-8" />

									{/* Totaux */}
									<div className="space-y-2">
										<div className="flex justify-end font-semibold text-blue-badge-text">
											<span>TOTAL DES MONTANTS DÉCLARÉS &nbsp;</span>
											<span>
												{(
													submissionData.participantToConstructionSiteSubmissions?.reduce(
														(sum, p) => {
															const amount = shouldDisplayHT
																? p.totalFeesHT
																: p.totalFeesTTC;
															return sum + (amount ?? 0);
														},
														0,
													) || 0
												).toLocaleString("fr-FR")}{" "}
												€ {shouldDisplayHT ? "HT" : "TTC"}
											</span>
										</div>

										{constructionSite?.constructionSiteDetails && (
											<div className="flex justify-end font-semibold text-blue-badge-text">
												<span>
													COÛT DE CONSTRUCTION PRÉVISIONNEL DÉCLARÉ &nbsp;
												</span>
												<span>
													{constructionSite.constructionSiteDetails
														.totalAmountTTC
														? constructionSite.constructionSiteDetails.totalAmountTTC.toLocaleString(
																"fr-FR",
															)
														: constructionSite.constructionSiteDetails.totalAmountHT?.toLocaleString(
																"fr-FR",
															) || "0"}{" "}
													€
												</span>
											</div>
										)}
									</div>
								</>
							) : (
								<div className="text-center py-8">
									<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-semibold text-gray-600 mb-2">
										Aucun intervenant enregistré
									</h3>
									<p className="text-gray-500 mb-4">
										Commencez la soumission pour voir la liste des intervenants
									</p>
									<Button
										onClick={() => {
											navigate(
												PAGES.PROJECTS.submission.withParams(
													constructionSiteId.toString(),
													1,
												),
											);
										}}
										variant="outline"
									>
										Commencer la soumission
									</Button>
								</div>
							)}
						</div>
					</TabsContent>

					<TabsContent value="reception" className="mt-4">
						<div className="space-y-6">
							{receptionSections &&
							(receptionSections.designers.length > 0 ||
								receptionSections.builders.length > 0 ||
								receptionSections.materials.length > 0) ? (
								<>
									<DesignersTableReception
										submissionParticipants={receptionSections.designers}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
										displayInsurer
									/>
									<Separator className="my-8" />
									<BuildersTableReception
										submissionParticipants={receptionSections.builders}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
										displayInsurer
									/>
									<Separator className="my-8" />
									<MaterialsTableReception
										submissionParticipants={receptionSections.materials}
										constructionSiteId={constructionSiteId}
										selectedParticipantId={selectedParticipantId}
										onParticipantSelect={handleParticipantSelect}
										onDocumentClick={handleDocumentClick}
										loadingDocumentId={loadingDocumentId}
									/>
								</>
							) : (
								<div className="text-center py-8">
									<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-semibold text-gray-600 mb-2">
										Aucun intervenant de réception
									</h3>
									<p className="text-gray-500 mb-4">
										Les intervenants de soumission seront copiés ici
										automatiquement à l'ouverture
									</p>
								</div>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

export default ParticipantsTab;
